import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  useMediaQuery,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import AuthHeader from "../components/AuthHeader";

function SkitViewerPage({ API_BASE, AUDIO_BASE }) {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [currentTranscription, setCurrentTranscription] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // 音声再生ログ関連
  const [currentPlayLog, setCurrentPlayLog] = useState(null);

  // ログイン状態チェック
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/skit_viewer' }, replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 音声/映像再生ログ開始
  const startPlayLog = async (transcription, audioFileName, isVideo = false) => {
    // db_user_idが無い場合はログアウトしてログイン画面へリダイレクト
    if (!user?.db_user_id) {
      console.warn('⚠️ db_user_idが無いため、ログアウトしてログイン画面へリダイレクトします。');
      logout();
      return;
    }

    try {
      // transcription.idがない場合はログを記録しない
      if (!transcription || !transcription.id) {
        console.error("★ transcription.id is missing, cannot log audio play");
        return;
      }

      // transcriptions.idをcontent_idとして使用
      const logData = {
        session_id: String(user.db_user_id),
        content_type: 'skit',
        content_id: transcription.id, // transcriptions.id
        audio_file_name: audioFileName,
        is_video: isVideo ? 1 : 0, // 映像の場合は1、音声の場合は0
        play_start_time: new Date().toISOString(),
        playback_rate: playbackRate
      };

      const response = await fetch(`${API_BASE}/audio_play_logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData)
      });

      const data = await response.json();

      if (data.success) {
        setCurrentPlayLog({
          log_id: data.log_id,
          start_time: new Date(),
          ...logData
        });
        console.log('★Skit再生ログ開始:', data.log_id, isVideo ? '(映像)' : '(音声)');
      }
    } catch (error) {
      console.error('★Skit再生ログ開始エラー:', error);
    }
  };

  // 音声再生ログ完了
  const completeCurrentPlayLog = async () => {
    if (!currentPlayLog) return;

    try {
      const endTime = new Date();
      const durationSeconds = (endTime - currentPlayLog.start_time) / 1000;

      const updateData = {
        ...currentPlayLog,
        play_end_time: endTime.toISOString(),
        duration_seconds: durationSeconds
      };

      await fetch(`${API_BASE}/audio_play_logs/${currentPlayLog.log_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      console.log('★Skit再生ログ完了:', durationSeconds, '秒');
      setCurrentPlayLog(null);
    } catch (error) {
      console.error('★Skit音声ログ完了エラー:', error);
    }
  };
  
  // 音声/映像切り替え処理
  const handleToggleChange = (event) => {
    const newShowVideo = event.target.checked;
    
    // 現在再生中のものを停止
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // 音声再生ログ完了
      if (currentPlayLog) {
        completeCurrentPlayLog();
      }
    }
    
    if (isVideoPlaying && videoRef.current) {
      videoRef.current.pause();
      setIsVideoPlaying(false);
      if (videoIntervalRef.current) {
        clearInterval(videoIntervalRef.current);
      }
      // 映像再生ログ完了
      if (currentPlayLog) {
        completeCurrentPlayLog();
      }
    }
    
    // 両方の時間を0にリセット
    setCurrentTime(0);
    setVideoCurrentTime(0);
    
    // モード切り替え
    setShowVideo(newShowVideo);
  };
  
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const intervalRef = useRef(null);
  const videoIntervalRef = useRef(null);

  // 音声ファイル名から動画ファイル名を生成
  const getVideoFileName = (audioFileName) => {
    if (!audioFileName) return null;

    // Koukiを含む場合の特別処理
    // 例: 01_Skit_Kouki1.mp3 → Skit_2nd_1.mp4
    if (audioFileName.includes('Kouki')) {
      const match = audioFileName.match(/Kouki(\d+)/);
      if (match) {
        const number = match[1]; // 最後の数値を取得
        return `Skit_2nd_${number}.mp4`;
      }
    }

    // 通常処理
    // 例: 41_Skit_1st8-2.mp3 → Skit_1st_8_2.mp4
    const parts = audioFileName.split('_');
    if (parts.length < 3) return null;

    const skitPart = parts[1]; // "Skit"
    const weekPart = parts[2].replace('.mp3', ''); // "1st8-2"

    // 1st8-2 → 1st_8_2 (英字の後の数字の前にアンダーバーを挿入し、ハイフンをアンダーバーに変換)
    const formattedWeekPart = weekPart.replace(/([a-zA-Z]+)(\d+)/, '$1_$2').replace(/-/g, '_');

    return `${skitPart}_${formattedWeekPart}.mp4`;
  };

  // week選択肢を取得
  useEffect(() => {
    const fetchWeekOptions = async () => {
      try {
        const response = await fetch(`${API_BASE}/skit_weeks`);
        const data = await response.json();
        
        setWeekOptions(data);
        
        // 最初の選択肢を自動選択
        if (data.length > 0) {
          setSelectedWeek(data[0].week_num);
        }
      } catch (error) {
        console.error('週選択肢の取得に失敗:', error);
      }
    };

    fetchWeekOptions();
  }, [API_BASE]);

  // 選択されたweekのtranscriptionデータを取得
  useEffect(() => {
    if (!selectedWeek) return;

    const fetchTranscription = async () => {
      try {
        const response = await fetch(`${API_BASE}/skit_by_week/${selectedWeek}`);
        const data = await response.json();
        
        if (data && data.length > 0) {
          setCurrentTranscription(data[0]);
        }
      } catch (error) {
        console.error('転写データの取得に失敗:', error);
      }
    };

    fetchTranscription();
  }, [selectedWeek, API_BASE]);

  // week変更ハンドラー
  const handleWeekChange = (event) => {
    setSelectedWeek(event.target.value);
    // 再生を停止
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      setCurrentTime(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // 音声再生ログ完了
      if (currentPlayLog) {
        completeCurrentPlayLog();
      }
    }
    // 現在のトランスクリプションをリセット
    setCurrentTranscription(null);
    setCurrentTime(0);
    setDuration(0);
    setVideoCurrentTime(0);
    setVideoDuration(0);
    setIsVideoPlaying(false);
    if (videoIntervalRef.current) {
      clearInterval(videoIntervalRef.current);
    }
    // 映像再生ログ完了
    if (currentPlayLog && isVideoPlaying) {
      completeCurrentPlayLog();
    }
  };

  // 再生速度変更時に音声・動画プレイヤーにも反映
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // 音声の再生/停止
  const handlePlay = async () => {
    if (!currentTranscription?.audio_file_name) {
      console.error('音声ファイルが選択されていません');
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // 音声再生ログ完了
      if (currentPlayLog) {
        await completeCurrentPlayLog();
      }
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        
        // 音声再生ログ開始
        await startPlayLog(currentTranscription, currentTranscription.audio_file_name, false);
        
        // 100msごとに現在時刻を更新
        intervalRef.current = setInterval(() => {
          const time = audioRef.current.currentTime;
          setCurrentTime(time);
        }, 100);
      } catch (error) {
        console.error('音声再生エラー:', error);
      }
    }
  };

  // シークバーの変更処理
  const handleSeekChange = (_, newValue) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  // 音声の長さを取得
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      audioRef.current.playbackRate = playbackRate;
    }
  };

  // 音声終了時の処理
  const handleAudioEnded = async () => {
    setIsPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    // 音声再生ログ完了
    if (currentPlayLog) {
      await completeCurrentPlayLog();
    }
  };

  // 動画の再生/停止
  const handleVideoPlay = async () => {
    if (!videoRef.current) return;

    if (isVideoPlaying) {
      videoRef.current.pause();
      setIsVideoPlaying(false);
      if (videoIntervalRef.current) {
        clearInterval(videoIntervalRef.current);
      }
      // 映像再生ログ完了
      if (currentPlayLog) {
        await completeCurrentPlayLog();
      }
    } else {
      try {
        await videoRef.current.play();
        setIsVideoPlaying(true);
        
        // 映像再生ログ開始
        await startPlayLog(currentTranscription, currentTranscription.audio_file_name, true);
        
        // 100msごとに現在時刻を更新
        videoIntervalRef.current = setInterval(() => {
          const time = videoRef.current.currentTime;
          setVideoCurrentTime(time);
        }, 100);
      } catch (error) {
        console.error('動画再生エラー:', error);
      }
    }
  };

  // 動画シークバーの変更処理
  const handleVideoSeekChange = (_, newValue) => {
    if (videoRef.current) {
      videoRef.current.currentTime = newValue;
      setVideoCurrentTime(newValue);
    }
  };

  // 動画の長さを取得
  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
      videoRef.current.playbackRate = playbackRate;
    }
  };

  // 映像終了時の処理
  const handleVideoEnded = async () => {
    setIsVideoPlaying(false);
    if (videoIntervalRef.current) {
      clearInterval(videoIntervalRef.current);
    }
    // 映像再生ログ完了
    if (currentPlayLog) {
      await completeCurrentPlayLog();
    }
  };

  // 音声終了時の処理
  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (videoIntervalRef.current) {
        clearInterval(videoIntervalRef.current);
      }
    };
  }, []);

  // ログイン中のみ表示
  if (!isAuthenticated) {
    return null; // リダイレクト中は何も表示しない
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="md" sx={{ paddingBottom: isSmallScreen ? 24 : 14 }}>

      {/* Week選択プルダウン */}
      <Box sx={{ marginBottom: 2 }}>
        <FormControl fullWidth>
          <InputLabel id="week-select-label">Week</InputLabel>
          <Select
            labelId="week-select-label"
            id="week-select"
            value={selectedWeek}
            label="Week"
            onChange={handleWeekChange}
          >
            {weekOptions.map((option) => (
              <MenuItem key={option.week_num} value={option.week_num}>
                Week {option.week_num}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* 音声コントロール */}
      {!showVideo && (
        <Paper sx={{ padding: 2, marginBottom: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
            <Button
              variant={isPlaying ? "outlined" : "contained"}
              color={isPlaying ? "error" : "primary"}
              onClick={handlePlay}
              size="large"
              disabled={!currentTranscription?.audio_file_name}
            >
              {isPlaying ? "停止" : "再生"}
            </Button>
            
            <Typography variant="body2" color="text.secondary">
              {currentTranscription?.audio_file_name || "音声ファイルが選択されていません"}
            </Typography>
          </Box>

          {/* シークバー */}
          {currentTranscription?.audio_file_name && (
            <Box sx={{ marginBottom: 2 }}>
              <Slider
                value={currentTime}
                onChange={handleSeekChange}
                min={0}
                max={duration || 100}
                step={0.1}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${Math.floor(value / 60)}:${(value % 60).toFixed(0).padStart(2, '0')}`}
                sx={{ width: '100%' }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'text.secondary' }}>
                <span>{Math.floor(currentTime / 60)}:{(currentTime % 60).toFixed(0).padStart(2, '0')}</span>
                <span>{Math.floor(duration / 60)}:{(duration % 60).toFixed(0).padStart(2, '0')}</span>
              </Box>
            </Box>
          )}

          {/* 音声プレイヤー（非表示） */}
          {currentTranscription?.audio_file_name && (
            <audio
              key={currentTranscription.audio_file_name}
              ref={audioRef}
              onEnded={handleAudioEnded}
              onLoadedMetadata={handleLoadedMetadata}
              onLoadedData={() => {
                console.log('音声ファイル読み込み完了:', currentTranscription.audio_file_name);
                if (audioRef.current) {
                  audioRef.current.playbackRate = playbackRate;
                }
              }}
              onError={() => console.error('音声ファイル読み込みエラー:', currentTranscription.audio_file_name)}
              style={{ display: 'none' }}
              preload="auto"
            >
              <source src={`${AUDIO_BASE}/audio/${currentTranscription.audio_file_name}`} type="audio/mpeg" />
            </audio>
          )}
        </Paper>
      )}

      {/* 動画プレイヤー */}
      {showVideo && currentTranscription?.audio_file_name && getVideoFileName(currentTranscription.audio_file_name) && (
        <Paper sx={{ padding: 2, marginBottom: 3 }}>
          
          <Box sx={{ marginBottom: 2 }}>
            <video
              ref={videoRef}
              width="100%"
              height="auto"
              controls={false}
              onEnded={handleVideoEnded}
              onLoadedMetadata={handleVideoLoadedMetadata}
              style={{ borderRadius: '8px' }}
            >
              <source src={`${API_BASE}/videos/${getVideoFileName(currentTranscription.audio_file_name)}`} type="video/mp4" />
            </video>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
            <Button
              variant={isVideoPlaying ? "outlined" : "contained"}
              color={isVideoPlaying ? "error" : "primary"}
              onClick={handleVideoPlay}
              size="large"
            >
              {isVideoPlaying ? "停止" : "再生"}
            </Button>
            
            <Typography variant="body2" color="text.secondary">
              {getVideoFileName(currentTranscription.audio_file_name)}
            </Typography>
          </Box>

          {/* 動画シークバー */}
          <Box sx={{ marginBottom: 2 }}>
            <Slider
              value={videoCurrentTime}
              onChange={handleVideoSeekChange}
              min={0}
              max={videoDuration || 100}
              step={0.1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.floor(value / 60)}:${(value % 60).toFixed(0).padStart(2, '0')}`}
              sx={{ width: '100%' }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'text.secondary' }}>
              <span>{Math.floor(videoCurrentTime / 60)}:{(videoCurrentTime % 60).toFixed(0).padStart(2, '0')}</span>
              <span>{Math.floor(videoDuration / 60)}:{(videoDuration % 60).toFixed(0).padStart(2, '0')}</span>
            </Box>
          </Box>
        </Paper>
      )}

      {/* デバッグ情報 */}
      {process.env.NODE_ENV === 'development' && (
        <Paper sx={{ padding: 2, marginBottom: 3 }}>
          <Typography variant="caption" color="text.secondary">
            現在時刻: {currentTime.toFixed(1)}s | 
            再生中: {isPlaying ? 'Yes' : 'No'} |
            再生速度: {playbackRate.toFixed(1)}x |
            選択Week: {selectedWeek}
          </Typography>
        </Paper>
      )}

      {/* 再生速度コントロール */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "#fff",
          borderTop: "1px solid #ccc",
          padding: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <Box minWidth={200} sx={{ marginBottom: 1 }}>
          <Typography gutterBottom>再生速度：{playbackRate.toFixed(1)}x</Typography>
          <Slider
            value={playbackRate}
            onChange={(_, newValue) => setPlaybackRate(newValue)}
            min={0.5}
            max={1.5}
            step={0.1}
            valueLabelDisplay="auto"
            marks={[
              { value: 0.5, label: "0.5x" },
              { value: 1.0, label: "1.0x" },
              { value: 1.5, label: "1.5x" }
            ]}
          />
        </Box>
        
        {/* 音声/映像切り替えトグル */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color={!showVideo ? "primary" : "text.secondary"}>
            🔊 音声
          </Typography>
          <Switch
            checked={showVideo}
            onChange={handleToggleChange}
            color="primary"
          />
          <Typography variant="body2" color={showVideo ? "primary" : "text.secondary"}>
            📹 映像
          </Typography>
        </Box>
      </Box>
      </Container>
    </>
  );
}

export default SkitViewerPage;